import React from 'react';
import { TouchableOpacity, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import MyAppText from '../../../../components/MyAppText';
import moment from 'moment';
import { translate } from '../../../../services/translate';
import Badge from '../../../../components/Badge';
import { DEFAULT_PASTEL_COLORS, DEFAULT_PASTEL_TEXT_COLORS } from '../../../../styles/colorPalettes';
import { ScrollView } from 'react-native-web-hover';
import { formatRelativeTime } from '../../../deac/DeacCommons';

interface Props {
    application: Application;
    onPress: (application: Application) => void;
}

export default function ApplicationCard({ application, onPress }: Props) {
    const { styles } = useStyles(styleSheet);
    const vacancy = application.vacancy;

    function getVacancyTagTextColor(color: string) {
        const index = DEFAULT_PASTEL_COLORS.indexOf(color);
        return DEFAULT_PASTEL_TEXT_COLORS[index];
    }

    function showPresenceInfo() {
        return application.deletedAt != null || moment().isAfter(moment(vacancy.activityStart));
    }

    function showCanceledJustification() {
        return application.deletedAt != null;
    }

    function getApplicationSituation() {
        if (application.present == null) {
            return 'pendingPresence';
        } else if (application.present) {
            return 'present';
        } else if (!application.present && !application.absentJustification) {
            return 'unjustifiedAbsence';
        }
        return 'absent';
    }

    return (
        <TouchableOpacity
            style={styles.card}
            onPress={() => onPress(application)}
        >
            <View style={styles.cardHeader}>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                    <MyAppText style={styles.cardTitle}>{vacancy.title}</MyAppText>
                    <MyAppText style={styles.cardTime}>{formatRelativeTime(application.createdAt)}</MyAppText>
                </View>
                <View style={{ flexDirection: 'column', rowGap: 4 }}>
                    <MyAppText style={styles.cardWorkplace}>{vacancy.workplace.name}</MyAppText>
                    <MyAppText style={styles.cardWorkplace}>{moment(vacancy.activityStart).format('DD/MM HH:mm')} - {moment(vacancy.activityFinish).format('DD/MM HH:mm')}</MyAppText>
                </View>
            </View>
            <ScrollView contentContainerStyle={styles.tagsContainer}>
                {vacancy.tags?.map(tag =>
                    <Badge
                        mode='ROUNDED'
                        key={tag.id}
                        backgroundColor={tag.color}
                        textColor={getVacancyTagTextColor(tag.color)}
                    >
                        {tag.name}
                    </Badge>
                )}
            </ScrollView>
            {showPresenceInfo()
                ? showCanceledJustification()
                    ? <MyAppText style={styles.canceledJustification}>{translate('reason')}:{' '}
                        <MyAppText style={styles.canceledJustificationText}>{application.cancellationReason?.name ?? ''}</MyAppText>
                    </MyAppText>
                    : <MyAppText style={styles.situation(getApplicationSituation())}>
                        {translate('situation')}:{' '}
                        <MyAppText style={styles.situationText(getApplicationSituation())}>{translate(getApplicationSituation()).toLowerCase()}</MyAppText>
                    </MyAppText>
                : null}
        </TouchableOpacity>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    card: {
        padding: 12,
        borderWidth: 1,
        borderColor: theme.colors.neutral['100'],
        borderRadius: 8,
        gap: 16,
        backgroundColor: theme.colors.white['500']
    },
    cardHeader: {
        flexDirection: 'column',
        rowGap: 8,
    },
    cardTitle: {
        fontSize: 14,
        fontWeight: '700',
        color: theme.colors.neutral['500']
    },
    cardTime: {
        fontSize: 10,
        fontWeight: '600',
        color: theme.colors.neutral['400']
    },
    cardWorkplace: {
        fontSize: 12,
        fontWeight: '600',
        color: theme.colors.neutral['400']
    },
    tagsContainer: {
        flex: 1,
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8,
        maxHeight: 80,
    },
    situation: (situation: 'pendingPresence' | 'present' | 'absent' | 'unjustifiedAbsence') => ({
        fontSize: 12,
        fontWeight: '700',
        color: (situation === 'absent' || situation === 'unjustifiedAbsence')
            ? theme.colors.errors['300']
            : situation === 'present'
                ? theme.colors.successes['300']
                : theme.colors.alert['300'],
    }),
    situationText: (situation: 'pendingPresence' | 'present' | 'absent' | 'unjustifiedAbsence') => ({
        fontSize: 12,
        fontWeight: '600',
        color: (situation === 'absent' || situation === 'unjustifiedAbsence')
            ? theme.colors.errors['300']
            : situation === 'present'
                ? theme.colors.successes['300']
                : theme.colors.alert['300'],
    }),
    canceledJustification: {
        fontSize: 12,
        fontWeight: '700',
        color: theme.colors.errors['300']
    },
    canceledJustificationText: {
        fontSize: 12,
        fontWeight: '600',
        color: theme.colors.errors['300']
    }
}));
